/**
 * Task Card Component - Jira-style task cards
 */
import { Task } from '@/types';
import { User, Calendar, AlertCircle, Clock, Pencil, Trash2, X, Check, CheckSquare } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { deleteTask, updateTask } from '@/store/taskSlice';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { userAPI } from '@/api/userAPI';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { TaskType, Priority } from '@/api/mastersApi';
import { API_BASE_URL } from '@/utils/constants';
import { AvatarTooltip } from '@/components/AvatarTooltip';
import { TaskTypeIcon } from '@/components/TaskTypeIcon';
import { PriorityIcon } from '@/components/PriorityIcon';
import toast from 'react-hot-toast';
import { sprintAPI, SprintWithTasks } from '@/api/sprintAPI';
// Subtasks are displayed only inside Task Modal per latest requirement

interface TaskCardProps {
  task: Task;
  project?: { // HIGHLIGHT: Add optional project prop
    id: string;
    key: string;
    name: string;
  };
  onClick?: () => void;
  onDelete?: () => void;
  onUpdate?: () => void;
  projectMembers?: any[];
  taskTypes?: TaskType[];
  priorities?: Priority[];
  /** When true, user is viewer-only in this project and cannot edit/delete */
  isViewerOnly?: boolean;
}

export const TaskCard = ({
  task,
  onClick,
  onDelete,
  onUpdate,
  project,
  projectMembers,
  taskTypes,
  priorities,
  isViewerOnly,
}: TaskCardProps) => {
  const dispatch = useAppDispatch();
  const { currentProject } = useAppSelector((state) => state.projects);
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [assigneeResolved, setAssigneeResolved] = useState<{ name: string; initials: string } | null>(null);
  const [localAssignedTo, setLocalAssignedTo] = useState<string | null | undefined>(undefined);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState(task.title);
  const [displayTitle, setDisplayTitle] = useState(task.title);
  const [sprintDetails, setSprintDetails]= useState<SprintWithTasks|null>(null);
  const [assigneePortalPos, setAssigneePortalPos] = useState<{ top: number; left: number; width: number } | null>(null);
  // Removed subtasks UI from card

  async function getSprintDetails(sprintId:string){
    try {
        const response =await sprintAPI.getSprint(sprintId);
        return response;
    } catch (error) {
      throw error;
    }
  }

  const handleTitleSave = async () => {
    const trimmed = newTitle.trim();
    const baseTitle = displayTitle || task.title;
    const nextTitle = trimmed || baseTitle;
    try {
      if (nextTitle !== baseTitle) {
        const updated = await dispatch(updateTask({ id: task.id, data: { title: nextTitle } })).unwrap();
        const latestTitle = updated?.title || nextTitle;
        setDisplayTitle(latestTitle);
        onUpdate?.();
      } else {
        setDisplayTitle(nextTitle);
      }
    } catch {
      // ignore
    } finally {
      setNewTitle(nextTitle);
      setIsEditingTitle(false);
    }
  };

  const handleTitleCancel = () => {
    setNewTitle(displayTitle);
    setIsEditingTitle(false);
  };

  // Confirm dialog state for delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);


  // If the parent passed members down, use them. Otherwise the list is
  // lazy-loaded the first time the user opens the assignee dropdown — see
  // ensureMembersLoaded() below. The Tasks page renders many cards, and the
  // assignee chip's *display* uses the denormalized task.assigned_to_name
  // returned by the backend, so members aren't needed for the initial paint.
  useEffect(() => {
    if (Array.isArray(projectMembers) && projectMembers.length > 0) {
      setMembers(projectMembers);
    }
  }, [JSON.stringify(projectMembers)]);

  useEffect(() => {
    setDisplayTitle(task.title);
    setNewTitle(task.title);
  }, [task.title]);

  // Lazy-loader for the assignee dropdown. Called only when the user clicks
  // to open the dropdown — not on card mount.
  const [membersLoading, setMembersLoading] = useState(false);
  const ensureMembersLoaded = async () => {
    if (members.length > 0 || membersLoading) return;
    const projectId = (task as any).project_id || project?.id || currentProject?.id;
    if (!projectId) return;
    setMembersLoading(true);
    try {
      const data = await projectMemberAPI.getProjectMembers(projectId);
      setMembers(data);
    } catch { } finally {
      setMembersLoading(false);
    }
  };

  // Sprint details are only needed when the card displays sprint context.
  // The task response now includes a denormalized `sprint_name`, so we only
  // call sprintAPI when richer details (dates, etc.) are actually rendered.
  useEffect(() => {
    if (!task.sprint_id) return;
    if ((task as any).sprint_name && !sprintDetails) {
      setSprintDetails({ id: task.sprint_id, name: (task as any).sprint_name } as any);
      return;
    }
    (async () => {
      try {
        const response = await getSprintDetails(task.sprint_id!);
        setSprintDetails(response);
      } catch (error) {
        toast.error("error in fetching the sprint details");
      }
    })();
  }, [task.sprint_id, (task as any).sprint_name]);

  const getTaskId = () => {
    // Priority: 1. Passed project, 2. currentProject, 3. Fallback to 'AHM'
    const projectKey = project?.key || currentProject?.key || 'AHM';
    return `${projectKey}-${(task as any).display_number ?? task.id?.substring(0, 6)}`;
  };

  const taskId = getTaskId();
  // Generate human-friendly task key like AHM-1 using display_number when available
  // const taskId = `${projectKey}-${(task as any).display_number ?? (task.id?.substring(0, 6) || '')}`;

  // Use actual task labels if available, otherwise show none
  // const taskLabels = task.labels ? task.labels.split(',').map(label => label.trim()).filter(label => label) : [];
  const taskLabels: string[] = Array.isArray(task.labels)
    ? (task.labels as string[]).filter(l => typeof l === 'string' && l.trim()).map(l => l.trim())
    : (typeof (task as any).labels === 'string'
      ? (task as any).labels.split(',').map((label: string) => label.trim()).filter((label: string) => label)
      : []);

  // Resolve assignee name. Resolution order:
  //   1. task.assigned_to_name (denormalized by the backend list endpoints)
  //   2. project members already loaded (e.g. lazily after dropdown was opened)
  //   3. userAPI.getUser fallback (cached + deduped at the API layer)
  // The backend now denormalizes assignee names onto the task response, so
  // path 1 covers the common case with zero network traffic.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!task.assigned_to) { setAssigneeResolved(null); return; }
      const assigneeId = String(task.assigned_to);
      let name: string | null | undefined = (task as any).assigned_to_name;
      if (!name) {
        const m = members.find(mm => String(mm.user_id) === assigneeId);
        name = m ? (m.user_display_name || m.username || m.user_email || assigneeId) : null;
      }
      // Fallback when neither denormalized data nor loaded members can resolve
      // the assignee (e.g. orphan ID after user deletion). userAPI has TTL +
      // in-flight dedup, so concurrent unresolved cards still collapse to one
      // HTTP call per unique user.
      if (!name) {
        try {
          const user = await userAPI.getUser(assigneeId);
          name = user.display_name || `${user.first_name} ${user.last_name}`.trim() || user.username || user.email || assigneeId;
        } catch { }
      }
      if (cancelled) return;
      if (name) {
        const parts = name.trim().split(/\s+/);
        const initials = parts.length === 1 ? parts[0].substring(0, 2).toUpperCase() : (parts[0][0] + parts[1][0]).toUpperCase();
        setAssigneeResolved({ name, initials });
      } else {
        setAssigneeResolved(null);
      }
    })();
    return () => { cancelled = true; };
  }, [task.assigned_to, (task as any).assigned_to_name, members]);

  const colorFromSeed = (seed: string) => {
    const colors = ['bg-red-500', 'bg-green-500', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500'];
    const sum = Array.from(seed).reduce((a, c) => a + c.charCodeAt(0), 0);
    return colors[sum % colors.length];
  };
  const initialsFromName = (value: string) => {
    if (!value) return 'U';
    const parts = value.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };
  const effectiveAssignedTo = localAssignedTo !== undefined ? localAssignedTo : (task.assigned_to as any);
  const assigneeColor = effectiveAssignedTo ? colorFromSeed(String(effectiveAssignedTo)) : 'bg-gray-500';
  const currentUserId = currentUser?.id ? String(currentUser.id) : null;
  const currentUserName =
    currentUser?.name ||
    currentUser?.display_name ||
    [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ').trim() ||
    currentUser?.username ||
    currentUser?.email ||
    'You';
  const currentUserInitials = initialsFromName(currentUserName);
  const currentUserColor = colorFromSeed(currentUserId || currentUser?.email || currentUserInitials);
  const isAssignedToCurrentUser = currentUserId ? String(effectiveAssignedTo ?? '') === currentUserId : false;

  // Get assignee display name
  const getAssigneeInfo = () => assigneeResolved || { initials: '', name: '' };

  // Removed unused tooltip/status helpers

  // Task type image helpers (same as Backlog)
  const resolveTaskTypeIconUrl = (raw?: string | null) => {
    if (!raw) return null;
    const builtInIcons = ['task', 'bug', 'story', 'epic', 'subtask'];
    if (builtInIcons.includes(raw.toLowerCase())) return null;
    try {
      if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
      const api = new URL(API_BASE_URL);
      const origin = `${api.protocol}//${api.host}`;
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return origin + path;
    } catch {
      return null;
    }
  };

  // Assignee data
  const assignees = [
    { id: null, name: 'Unassigned', initials: '', email: '', color: 'bg-gray-500' },
    ...members.map((m) => ({
      id: m.user_id,
      name: m.user_display_name || m.username || m.user_email || 'User',
      initials: initialsFromName(m.user_display_name || m.username || m.user_email || 'U'),
      email: m.user_email,
      color: colorFromSeed(m.user_id || m.user_email || 'U')
    }))
  ];

  const handleAssigneeChange = async (newAssigneeId: string | null) => {
    try {
      await dispatch(updateTask({
        id: task.id,
        data: { assigned_to: newAssigneeId === null ? null : newAssigneeId }
      })).unwrap();
      // Optimistic local UI update for immediate feedback
      setLocalAssignedTo(newAssigneeId);
      const chosen = (newAssigneeId && members.find(mm => String(mm.user_id) === String(newAssigneeId))) || null;
      if (chosen) {
        const name = chosen.user_display_name || chosen.username || chosen.user_email || String(newAssigneeId);
        const initials = (name.trim().split(/\s+/).map((p:any)=>p[0]).join('').substring(0,2) || 'U').toUpperCase();
        setAssigneeResolved({ name, initials });
      } else {
        setAssigneeResolved(null);
      }
      onUpdate?.();
      setShowAssigneeDropdown(false);
    } catch (error) {
      console.error('Failed to update assignee:', error);
    }
  };

  const filteredAssignees = assignees.filter(assignee =>
    assignee.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(target);
      const inPortal = !!target.closest('[data-assignee-portal="true"]');
      if (!inTrigger && !inPortal) {
        setShowAssigneeDropdown(false);
      }
    };

    if (showAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAssigneeDropdown]);

  // Compute position for portal dropdown and update on scroll/resize
  useEffect(() => {
    const compute = () => {
      if (!dropdownRef.current) return;
      const rect = dropdownRef.current.getBoundingClientRect();
      const width = 256; // w-64
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      const top = Math.min(rect.bottom + 4, window.innerHeight - 8);
      setAssigneePortalPos({ top, left, width });
    };
    if (showAssigneeDropdown) {
      compute();
      window.addEventListener('scroll', compute, true);
      window.addEventListener('resize', compute);
      return () => {
        window.removeEventListener('scroll', compute, true);
        window.removeEventListener('resize', compute);
      };
    }
  }, [showAssigneeDropdown]);

  const handleCardClick = () => {
    onClick?.();
  };

  const sprintStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-50 text-green-600 border-green-200';
      case 'completed': return 'bg-gray-100 text-gray-500 border-gray-200';
      default: return 'bg-amber-50 text-amber-600 border-amber-200'; // planning, etc.
    }
  };

  return (
    <div
      className="group bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-all mb-0 w-full cursor-pointer"
      onClick={handleCardClick}
      draggable
    >
      {/* Optional cover image (first attachment/cover_url) */}
      {(() => {
        const cover = (task as any)?.cover_url || (Array.isArray((task as any)?.attachments) ? (task as any).attachments[0]?.url : null);
        if (!cover) return null;
        return (
          <div className="-mx-3 -mt-3 mb-3">
            <img src={cover} alt="attachment" className="w-full h-28 object-cover rounded-t-md" />
          </div>
        );
      })()}

      {sprintDetails && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-violet-700 leading-tight max-w-[160px] truncate">
            {/* Sprint icon: lightning bolt */}
            <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 1L3 7h4l-2 4 6-6H7l2-4z" fill="currentColor"/>
            </svg>
            <span className="truncate">{sprintDetails.name}</span>
          </span>
          {sprintDetails.status && (
            <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize ${sprintStatusStyle(sprintDetails.status)}`}>
              {sprintDetails.status}
            </span>
          )}
        </div>
      )}

      {/* Task Title with hover edit */}
      <div className="flex items-start justify-between gap-2 mb-2">
        {isEditingTitle ? (
          <div
            className="w-full"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleTitleSave();
                } else if (e.key === 'Escape') {
                  handleTitleCancel();
                }
              }}
              onBlur={() => {
                if (!newTitle.trim()) {
                  handleTitleCancel();
                  return;
                }
                handleTitleSave();
              }}
              autoFocus
              onClick={(e) => e.stopPropagation()}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleTitleSave(); }}
               className="p-1.5 hover:bg-green-100 rounded text-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
                
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleTitleCancel(); }}
                 className="p-1.5 hover:bg-red-100 rounded text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                
              </button>
            </div>
          </div>
        ) : (
          <h4 className="text-[15px] font-semibold text-gray-900 leading-6 whitespace-pre-wrap break-words break-all">
            {displayTitle.replace(/[✔✓]/g, '').trim()}
          </h4>
        )}
        {!isEditingTitle && !isViewerOnly && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              className="p-1 rounded hover:bg-gray-100"
              title="Edit title"
              onClick={(e) => { e.stopPropagation(); setNewTitle(displayTitle); setIsEditingTitle(true); }}
            >
              <Pencil className="w-4 h-4 text-gray-600" />
            </button>
            <button
              className="p-1 rounded hover:bg-red-50"
              title="Delete task"
              onClick={(e) => { 
                e.stopPropagation(); 
                setShowDeleteConfirm(true); 
              }}
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        )}
      </div>

      {/* Status tooltip removed */}

      {/* Labels */}
      {taskLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {taskLabels.map((label: string, index: number) => (
            <span
              key={index}
              className="bg-purple-100 text-purple-800 text-[11px] px-2 py-0.5 rounded font-medium uppercase tracking-wide"
            >
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Due Date inline pill (only when set) */}
      {task?.due_date && (
        <div className="mb-2">
          {(() => {
            const due = new Date(task.due_date as any);
            const today = new Date();
            const diffDays = Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            const cls = diffDays < 0
              ? 'text-red-700 bg-red-100 border-red-300'
              : (diffDays <= 1 ? 'text-orange-700 bg-orange-100 border-orange-300' : 'text-gray-700 bg-gray-100 border-gray-300');
            const Icon = diffDays < 0 ? AlertCircle : (diffDays <= 1 ? Clock : Calendar);
            return (
              <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${cls}`}>
                <Icon className="w-3.5 h-3.5" /> {due.toLocaleDateString()}
              </span>
            );
          })()}
        </div>
      )}


      {/* Subtasks moved to Task Modal only */}

      {/* Task Description/Content */}
      {/* {task.description && (
        <div className="mb-3">
          <p className="text-xs text-gray-600 line-clamp-2">
            {task.description}
          </p>
        </div>
      )} */}

      {/* Footer: story key, status/estimate, assignee */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1 text-[12px] text-gray-700">
          {(() => {
            if (!taskTypes || taskTypes.length === 0) {
              // Return a stable fallback when taskTypes are not loaded yet
              return <CheckSquare className="w-4 h-4 flex-shrink-0 text-blue-600" />;
            }
            const id = (task as any).task_type_id;
            const nameFromTask = (task as any).task_type ? String((task as any).task_type).toLowerCase() : '';
            const match = taskTypes.find(tt => {
              if (id && String(tt.id) === String(id)) return true;
              const n = (tt.name || '').toLowerCase();
              return nameFromTask && n === nameFromTask;
            });
            const iconRaw = (match as any)?.icon || (match as any)?.icon_url || (match as any)?.image || (match as any)?.image_url;
            const iconUrl = resolveTaskTypeIconUrl(iconRaw);
            const n = (match?.name || nameFromTask || 'task').toLowerCase();
            return <TaskTypeIcon url={iconUrl} name={n} className="w-4 h-4 flex-shrink-0" />;
          })()}
          <span className="font-medium">{taskId}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Done check (if completed) */}
          {(() => {
            const status = String((task as any).status || '').toLowerCase();
            const isDone = status.includes('done') || status.includes('completed');
            return isDone ? <span className="text-green-600" title="Done">✓</span> : null;
          })()}
          {/* Estimate/time pill */}
          <span className="text-[12px] font-medium px-2 py-0.5 rounded-md border border-gray-300 text-gray-800 bg-white">{(task as any).time_spent_minutes ?? 0}m</span>

          {/* Priority Icon */}
          {(() => {
            const priorityId = (task as any)?.priority_id;
            if (!priorityId || !priorities) return null;
            const priority = priorities.find(p => String(p.id) === String(priorityId));
            if (!priority) return null;
            return (
              <AvatarTooltip
                label={`${priority.name} Priority`}
                position="bottom"
              >
                <PriorityIcon
                  priority={priority}
                  size={16}
                  className="cursor-pointer"
                />
              </AvatarTooltip>
            );
          })()}

          {/* Assignee */}
          <div className="relative flex items-center" ref={dropdownRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const next = !showAssigneeDropdown;
              setShowAssigneeDropdown(next);
              // Lazy-load project members the first time the dropdown opens.
              // Avoids fetching members for every card on the Tasks page.
              if (next) void ensureMembersLoaded();
            }}
            className="flex items-center gap-2 hover:bg-gray-50 rounded-full p-0.5 transition-colors"
          >
            {task.assigned_to ? (
              <AvatarTooltip label={getAssigneeInfo().name || 'Unassigned'} position="bottom">
                <div
                  className={`w-7 h-7 ${assigneeColor} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                >
                  {getAssigneeInfo().initials}
                </div>
              </AvatarTooltip>
            ) : (
              <AvatarTooltip label="Unassigned" position="bottom">
                <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-500" />
                </div>
              </AvatarTooltip>
            )}
          </button>
          {/* Assignee Dropdown via portal to avoid clipping */}
            {showAssigneeDropdown && assigneePortalPos && createPortal(
              (
                <div
                  className="w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-[1000]"
                  style={{ position: 'fixed', top: assigneePortalPos.top, left: assigneePortalPos.left }}
                  data-assignee-portal="true"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-2 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search assignees..."
                      value={assigneeSearch}
                      onChange={(e) => setAssigneeSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  {currentUserId && (
                    <div className="border-b border-gray-100">
                      <button
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors ${isAssignedToCurrentUser ? 'bg-blue-50' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssigneeChange(currentUserId);
                        }}
                        disabled={isAssignedToCurrentUser}
                      >
                        <div
                          className={`w-6 h-6 ${currentUserColor} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                        >
                          {currentUserInitials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="truncate">Assign to Me</div>
                          <div className="text-xs text-gray-500 truncate">{currentUserName}</div>
                        </div>
                        {isAssignedToCurrentUser && <Check className="w-4 h-4 text-blue-600" />}
                      </button>
                    </div>
                  )}
                  <div className="max-h-48 overflow-y-auto">
                    {filteredAssignees.map((assignee) => (
                      <button
                        key={assignee.id || 'unassigned'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssigneeChange(assignee.id);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${String(task.assigned_to) === String(assignee.id) ? 'bg-blue-50' : ''}`}
                      >
                        {assignee.id ? (
                          <AvatarTooltip label={assignee.name} position="bottom">
                            <div
                              className={`w-6 h-6 ${assignee.color} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                            >
                              {assignee.initials}
                            </div>
                          </AvatarTooltip>
                        ) : (
                          <AvatarTooltip label="Unassigned" position="bottom">
                            <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-500" />
                            </div>
                          </AvatarTooltip>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{assignee.name}</div>
                          {assignee.email && <div className="text-xs text-gray-500 truncate">{assignee.email}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ),
              document.body
            )}
          </div>
        </div>
      </div>

      {/* Confirm Dialog for Delete */}
      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        itemName={task.title}
        itemType="task"
        onConfirm={async () => {
          if (isViewerOnly) {
            toast.error('You do not have permission to delete tasks in this project.');
            setShowDeleteConfirm(false);
            return;
          }

          setIsDeleting(true);
          try {
            await dispatch(deleteTask(task.id)).unwrap();
            onDelete?.();
          } catch (error) {
            console.error('Failed to delete task:', error);
          } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
          }
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        isLoading={isDeleting}
      />
      {/* No subtask modal from card */}
    </div>
  );
};